import React from 'react'

import {api} from '../services/api'
import {useRequiredAuth} from '../stores/AuthStore'
import {useSessions} from '../queries/useSessions'
import {useDivisions} from '../queries/useDivisions'
import {useBunks} from '../queries/useBunks'
import {useBunkPlans} from '../queries/useBunkPlans'
import {ReportFilterSelection, ReportRow} from '../types'
import {nameMapFrom} from '../utils'
import {joinNames} from '../views/TaggedPhotoReportView/formatters'

export type ReportStatus = 'idle' | 'loading' | 'empty' | 'error' | 'ready'

export interface TaggedPhotoReport {
  run: (filters: ReportFilterSelection) => Promise<void>
  status: ReportStatus
  error: string | null
  rows: ReportRow[]
  showBunkColumn: boolean
}

/**
 * Runs the Tagged Photo Report: resolve the filtered camper set, fetch coverage
 * for it, compose the printable rows, and raise the print dialog.
 *
 * The season is never a parameter — it comes from the camp's own record
 * (`client.currentSeasonID`, D-D/AC-4), which is the value every other
 * season-scoped query in this app already passes.
 *
 * Nothing here throws to the caller: both service calls return the uniform
 * APIResponse union, and every failure resolves to a status the view has copy
 * for. AC-12 through AC-15 all turn on the same rule — the print dialog is
 * raised in exactly one place, and only after both reads have succeeded with a
 * non-empty roster.
 */
export const useTaggedPhotoReport = (): TaggedPhotoReport => {
  const {client} = useRequiredAuth()
  const clientId = Number(client.ID)
  const season = client.currentSeasonID

  const sessionsQuery = useSessions({season})
  const divisionsQuery = useDivisions()
  const bunksQuery = useBunks({season})
  const bunkPlansQuery = useBunkPlans({season})

  const [status, setStatus] = React.useState<ReportStatus>('idle')
  const [error, setError] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<ReportRow[]>([])
  const [showBunkColumn, setShowBunkColumn] = React.useState(false)
  // Incremented once per successful run. The print dialog is raised from an
  // EFFECT keyed on this rather than inline in `run`, so React has committed the
  // composed table to the DOM before the browser snapshots the page — calling
  // window.print() inline prints the previous render.
  const [printToken, setPrintToken] = React.useState(0)

  React.useEffect(() => {
    if (printToken > 0) {
      window.print()
    }
  }, [printToken])

  const run = React.useCallback(
    async (filters: ReportFilterSelection): Promise<void> => {
      setStatus('loading')
      setError(null)

      const sessionNames = nameMapFrom(sessionsQuery.data?.pages)
      const divisionNames = nameMapFrom(divisionsQuery.data?.pages)
      const bunkNames = nameMapFrom(bunksQuery.data?.pages)

      // The bunks service requires the plan's bunk ids alongside the plan id;
      // the bunk-plans list the filter is built from already carries them.
      const selectedPlan = (bunkPlansQuery.data?.pages ?? [])
        .flatMap((page) => page.results)
        .find((plan) => Number(plan.ID) === filters.bunkPlanId)
      const bunkIds = (selectedPlan?.bunkIDs ?? []).map(Number)

      const rosterResponse = await api.roster.fetchReportRoster({
        clientId,
        season,
        sessionIds: filters.sessionIds,
        divisionIds: filters.divisionIds,
        bunkPlanId: filters.bunkPlanId ?? undefined,
        bunkIds,
      })

      if (rosterResponse.result === 'failure') {
        setRows([])
        setError(rosterResponse.error.message)
        setStatus('error')
        return
      }

      if (rosterResponse.data.length === 0) {
        setRows([])
        setStatus('empty')
        return
      }

      const coverageResponse = await api.faces.getTaggingCoverage({
        clientId,
        season,
        personIds: rosterResponse.data.map((camper) => camper.personId),
      })

      if (coverageResponse.result === 'failure') {
        setRows([])
        setError(coverageResponse.error.message)
        setStatus('error')
        return
      }

      const coverageByPerson = new Map(
        coverageResponse.data.results.map((c) => [c.personId, c]),
      )

      const composed: ReportRow[] = rosterResponse.data.map((camper) => {
        const coverage = coverageByPerson.get(camper.personId)
        return {
          personId: camper.personId,
          firstName: camper.firstName,
          lastName: camper.lastName,
          sessions: joinNames(camper.sessionIds, sessionNames),
          divisions: joinNames(camper.divisionIds, divisionNames),
          bunk:
            camper.bunkId === null ? '' : bunkNames.get(camper.bunkId) ?? '',
          // A camper the coverage service returned no row for is untrained with
          // no tags as far as this report can tell. Dropping the row instead
          // would understate exactly the population the report exists to find.
          trained: coverage?.trained ?? false,
          taggedPhotoCount: coverage?.taggedPhotoCount ?? 0,
          lastTaggedUTC: coverage?.lastTaggedUTC ?? null,
          lastTaggedPhotoCreatedUTC:
            coverage?.lastTaggedPhotoCreatedUTC ?? null,
        }
      })

      setRows(composed)
      setShowBunkColumn(filters.bunkPlanId !== null)
      setStatus('ready')
      setPrintToken((token) => token + 1)
    },
    [
      bunkPlansQuery.data,
      bunksQuery.data,
      clientId,
      divisionsQuery.data,
      season,
      sessionsQuery.data,
    ],
  )

  return {run, status, error, rows, showBunkColumn}
}
